import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { CreditDeletionModal } from '../../components/CascadeDeletionModal';
import {
	Section,
	StatStrip,
	DataTable,
	IdChip,
	Pill,
	RowActionsMenu,
	Button,
	ExportModal,
	buildXlsxExport,
	exportAmount,
} from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function creditEstado(credit) {
	if ((credit.saldo || 0) <= 0 && credit.cuotasTotal > 0) {
		return 'pagada';
	}
	if (credit.endDate && moment.utc(credit.endDate).isBefore(moment.utc())) {
		return 'vencida';
	}
	return 'activa';
}

const ESTADO_TONE = { pagada: 'ok', vencida: 'danger', activa: 'accent' };
const ESTADO_LABEL = { pagada: 'Pagada', vencida: 'Vencida', activa: 'Activa' };

function AccountCredits({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingCredit, setDeletingCredit] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'id',
		accessors: { id: (credit) => credit.customId },
	});

	const returnTo = () => encodeURIComponent(location.pathname + location.search);

	const handleCreateCredit = () => {
		navigate(`/AdminScreen/accounts/account/${accountId}/newCredit?returnTo=${returnTo()}`);
	};

	const handleEditCredit = (credit) => {
		navigate(`/AdminScreen/credits/editCredit/${credit._id}?returnTo=${returnTo()}`);
	};

	const stats = useMemo(() => {
		const pendientes = rows.filter((credit) => creditEstado(credit) !== 'pagada').length;
		const totalByCurrency = new Map();
		rows.forEach((credit) => {
			if (creditEstado(credit) !== 'pagada') {
				const key = credit.currency || '—';
				totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (credit.saldo || 0));
			}
		});
		return [
			{ label: 'Pendientes', value: pendientes },
			{
				label: 'Total pendiente',
				value: [...totalByCurrency.entries()].map(([currency, saldo]) => formatMoney(currency, saldo)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows]);


	const columns = [
		{ key: 'id', header: 'ID', sortable: true, render: (credit) => <IdChip>{credit.customId}</IdChip> },
		{ key: 'concept', header: 'Concepto', render: (credit) => credit.concept },
		{ key: 'date', header: 'Período', render: (credit) => formatDate(credit.date) },
		{ key: 'endDate', header: 'Vence', render: (credit) => formatDate(credit.endDate) },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right',
			render: (credit) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(credit.currency, credit.amount)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (credit) => {
				const estado = creditEstado(credit);
				return <Pill tone={ESTADO_TONE[estado]}>{ESTADO_LABEL[estado]}</Pill>;
			},
		},
	];

	const exportFields = [
		{ key: 'id', label: 'ID', locked: true },
		{ key: 'concept', label: 'Concepto' },
		{ key: 'date', label: 'Período' },
		{ key: 'endDate', label: 'Vence' },
		{ key: 'currency', label: 'Moneda' },
		{ key: 'amount', label: 'Monto' },
		{ key: 'estado', label: 'Estado' },
	];

	const exportGetters = {
		id: (credit) => credit.customId || '',
		concept: (credit) => credit.concept || '',
		date: (credit) => formatDate(credit.date),
		endDate: (credit) => formatDate(credit.endDate),
		currency: (credit) => credit.currency || '',
		amount: (credit) => exportAmount(credit.amount),
		estado: (credit) => ESTADO_LABEL[creditEstado(credit)] || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Pasividades',
			filePrefix: 'pasividades',
		});

	const renderRowActions = (credit) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => handleEditCredit(credit) },
				{ label: 'Eliminar', onClick: () => setDeletingCredit(credit), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleCreateCredit}>
					Nueva pasividad
				</Button>
			</div>
			<Section title='Pasividades'>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(credit) => navigate(`/AdminScreen/credits/credit/${credit._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron pasividades para esta cuenta.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Pasividades de esta cuenta'
				onExport={handleExport}
			/>

			<CreditDeletionModal
				show={Boolean(deletingCredit)}
				record={deletingCredit}
				onClose={() => setDeletingCredit(null)}
				onDeleted={() => {
					setDeletingCredit(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AccountCredits;
